import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { BaseModalComponent } from '../base-modal/base-modal.component';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';
import { Project } from '../../../dtos/project';
import { ProjectService } from '../../../services/project.service';
import { ToastService } from '../../../services/toast.service';
import { ProjectCreateUpdate } from '../../../dtos/project-create-update';

@Component({
  selector: 'app-project-edit-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, BaseModalComponent, ConfirmModalComponent],
  templateUrl: './project-edit-modal.component.html',
  styleUrls: ['./project-edit-modal.component.scss'],
})
export class ProjectEditModalComponent implements OnChanges {
  @Input() open = false;
  @Input() project: Project | null = null;
  @Output() openChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<Project>();
  @Output() deleted = new EventEmitter<string>();

  editForm: UntypedFormGroup;
  submitted = false;
  saving = false;
  deleting = false;
  showDeleteConfirm = false;
  errorMessage = '';

  constructor(
    private fb: UntypedFormBuilder,
    private projectService: ProjectService,
    private toastService: ToastService,
  ) {
    this.editForm = this.fb.group({
      title: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(255)]],
      description: ['', [Validators.maxLength(1000)]],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['project'] && this.project) {
      this.editForm.patchValue({
        title: this.project.title,
        description: this.project.description || '',
      });
    }

    if (changes['open'] && this.open && this.project) {
      this.submitted = false;
      this.errorMessage = '';
      this.editForm.patchValue({
        title: this.project.title,
        description: this.project.description || '',
      });
    }
  }

  close(): void {
    this.open = false;
    this.submitted = false;
    this.saving = false;
    this.deleting = false;
    this.showDeleteConfirm = false;
    this.errorMessage = '';
    this.openChange.emit(false);
  }

  get deleteConfirmMessage(): string {
    const title = this.project?.title ?? 'this project';
    return `The project "${title}" will be permanently removed. This action cannot be undone.`;
  }

  askDelete(): void {
    this.showDeleteConfirm = true;
  }

  cancelDelete(): void {
    this.showDeleteConfirm = false;
  }

  confirmDelete(): void {
    if (!this.project || this.deleting) {
      return;
    }

    this.deleting = true;
    this.errorMessage = '';

    this.projectService.deleteProject(this.project.id).subscribe({
      next: () => {
        this.toastService.success(
          'Project Deleted',
          `"${this.project!.title}" has been deleted successfully`,
        );
        this.deleted.emit(this.project!.id);
        this.close();
      },
      error: (error) => {
        console.error('Could not delete project', error);
        this.errorMessage =
          typeof error?.error === 'string'
            ? error.error
            : error?.error?.message || 'Could not delete the project. Please try again.';
        this.toastService.error('Failed to Delete Project', this.errorMessage);
        this.deleting = false;
        this.showDeleteConfirm = false;
      },
    });
  }

  save(): void {
    this.submitted = true;

    if (this.editForm.invalid || this.saving || this.deleting || !this.project) {
      return;
    }

    this.saving = true;
    this.errorMessage = '';

    const values = this.editForm.getRawValue();
    const request: ProjectCreateUpdate = {
      groupId: this.project.groupId,
      title: values.title.trim(),
      description: values.description?.trim() || undefined,
      // This modal does not edit inclusion/exclusion criteria; carry the existing values through
      // so a save does not wipe them.
      inclusionCriteria: this.project.inclusionCriteria ?? undefined,
      exclusionCriteria: this.project.exclusionCriteria ?? undefined,
      cohenKappaThreshold: this.project.cohenKappaThreshold ?? null,
    };

    this.projectService.updateProject(this.project.id, request).subscribe({
      next: (updatedProject) => {
        this.toastService.success(
          'Project Updated',
          'Project details have been updated successfully',
        );
        this.saved.emit(updatedProject);
        this.close();
      },
      error: (error) => {
        console.error('Could not update project', error);
        this.errorMessage =
          typeof error?.error === 'string'
            ? error.error
            : error?.error?.message || 'Could not update project. Please try again.';
        this.toastService.error('Failed to Update Project', this.errorMessage);
        this.saving = false;
      },
    });
  }
}
